import { addDays } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProgressChartSection } from 'shared/components/ui/progress-chart-section';
import { ProgressCountSection } from 'shared/components/ui/progress-count-section';
import { Section } from 'shared/components/ui/section';
import { useToday } from 'shared/contexts/today';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { GoalMetric } from 'shared/types/goal-metric';
import {
  countMetricOptions,
  DefaultMetricOptions,
  metricUnitFormatBase,
} from 'shared/types/metric-template';
import { WeekDays } from 'shared/types/week-days';
import { getLocalizedStringValue } from 'shared/utils/get-localized-string-value';

import * as Styled from './goal-template-preview-progress.style';

export type GoalTemplatePreviewProgressProps = {
  metric: GoalMetric | null;
  totalTaskCount: number;
  totalSubGoalCount: number;
  daysTillDeadline: number | null;
  weekStartsOn: WeekDays;
};

export const GoalTemplatePreviewProgress: React.FC<
  GoalTemplatePreviewProgressProps
> = ({
  metric,
  totalTaskCount,
  totalSubGoalCount,
  daysTillDeadline,
  weekStartsOn,
}) => {
  const today = useToday();
  const { t } = useTranslation();
  const locale = useI18nLocale();

  const unitFormat = useMemo(() => {
    if (metric?.unit?.format) {
      return (
        metric.unit.format ??
        `${metricUnitFormatBase} ${getLocalizedStringValue(metric.unit.name, locale)}`
      );
    }

    if (metric?.id === DefaultMetricOptions.CompletedTasks) {
      return `${metricUnitFormatBase} ${t('general.labels.goal-progress.unit-format.tasks.label')}`;
    }

    if (metric?.id === DefaultMetricOptions.SubGoalProgress) {
      return `${metricUnitFormatBase} ${t('general.labels.goal-progress.unit-format.sub-goals.label')}`;
    }

    return undefined;
  }, [metric?.id, metric?.unit, locale, t]);

  const deadline = useMemo(
    () =>
      typeof daysTillDeadline === 'number'
        ? addDays(today, daysTillDeadline)
        : undefined,
    [daysTillDeadline, today],
  );

  if (!metric) {
    return null;
  }

  return (
    <Section
      title={t('pages.goal-detail.progress.title')}
      canCollapse
      initialOpen
    >
      <Styled.Container>
        {countMetricOptions.includes(metric.id) ? (
          <ProgressCountSection
            target={
              metric?.id === DefaultMetricOptions.CompletedTasks
                ? totalTaskCount
                : totalSubGoalCount
            }
            targetFormat={
              unitFormat
                ? getLocalizedStringValue(unitFormat, locale)
                : undefined
            }
            total={100}
            completed={0}
          />
        ) : (
          <ProgressChartSection
            data={[]}
            startDate={today}
            endDate={deadline}
            targetFormat={
              unitFormat
                ? getLocalizedStringValue(unitFormat, locale)
                : undefined
            }
            startValue={metric?.startValue}
            target={metric?.targetValue ?? undefined}
            weekStartsOn={weekStartsOn}
          />
        )}
      </Styled.Container>
    </Section>
  );
};
